import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card, Typography, Row, Col, Select, Input, Button, Space, Spin,
  message, Tag, Popconfirm, Alert
} from 'antd';
import {
  SaveOutlined, ReloadOutlined, DeleteOutlined, CalendarOutlined
} from '@ant-design/icons';

import { mottosAPI } from '../utils/api';

const { Title, Text, Paragraph } = Typography;

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

// Genera un rango de años [current-1, current, current+1, current+2].
const buildYearOptions = () => {
  const cur = new Date().getFullYear();
  return [cur - 1, cur, cur + 1, cur + 2].map(y => ({ value: y, label: String(y) }));
};

const MottosPage = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState({});      // { 1: {id, text, ...}, 2: {...} }
  const [drafts, setDrafts] = useState({});  // { 1: 'texto editado', 2: '...' }
  const [savingMonth, setSavingMonth] = useState(null);
  const [loading, setLoading] = useState(false);

  const yearOptions = useMemo(buildYearOptions, []);

  const load = useCallback(async (y) => {
    setLoading(true);
    try {
      const res = await mottosAPI.listByYear(y);
      if (res.success) {
        const byMonth = {};
        const draftsInit = {};
        (res.data || []).forEach(r => {
          byMonth[r.month] = r;
          draftsInit[r.month] = r.text;
        });
        setRows(byMonth);
        setDrafts(draftsInit);
      } else {
        message.error('No se pudieron cargar los lemas');
      }
    } catch (err) {
      console.error(err);
      message.error('Error de red cargando lemas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(year); }, [year, load]);

  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();

  const isDirty = (month) => {
    const original = rows[month]?.text || '';
    return (drafts[month] || '') !== original;
  };

  const handleSave = async (month) => {
    const text = (drafts[month] || '').trim();
    if (!text) {
      message.warning('El lema no puede estar vacío');
      return;
    }
    setSavingMonth(month);
    try {
      const res = await mottosAPI.upsert(year, month, text);
      if (res.success) {
        setRows(prev => ({ ...prev, [month]: res.data }));
        message.success(`${MONTH_NAMES[month - 1]} guardado`);
      } else {
        message.error(res.error || 'Error al guardar');
      }
    } catch (err) {
      console.error(err);
      message.error(err?.response?.data?.error || 'Error de red al guardar');
    } finally {
      setSavingMonth(null);
    }
  };

  const handleDelete = async (month) => {
    setSavingMonth(month);
    try {
      const res = await mottosAPI.remove(year, month);
      if (res.success) {
        setRows(prev => {
          const next = { ...prev };
          delete next[month];
          return next;
        });
        setDrafts(prev => ({ ...prev, [month]: '' }));
        message.success(`${MONTH_NAMES[month - 1]} eliminado`);
      } else {
        message.error(res.error || 'Error al eliminar');
      }
    } catch (err) {
      console.error(err);
      message.error(err?.response?.data?.error || 'Error de red al eliminar');
    } finally {
      setSavingMonth(null);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <Row className="cms-header-row" justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            <CalendarOutlined /> Lemas del mes
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Textos que aparecen bajo el logo en el header de la app. Uno por mes.
          </Paragraph>
        </Col>
        <Col>
          <Space className="cms-actions-inline mottos-year">
            <Text strong>Año</Text>
            <Select
              value={year}
              options={yearOptions}
              onChange={setYear}
              style={{ width: 120 }}
            />
            <Button icon={<ReloadOutlined />} onClick={() => load(year)} loading={loading}>
              Actualizar
            </Button>
          </Space>
        </Col>
      </Row>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Cómo funciona"
        description={
          <div>
            <div>· Escribe el lema para cada mes y pulsa <strong>Guardar</strong> en esa fila.</div>
            <div>· Los cambios se aplican en cuanto la app hace la próxima consulta al servidor (hasta unos minutos por el cache).</div>
            <div>· Si un mes queda vacío, la app muestra un texto por defecto (fallback).</div>
          </div>
        }
      />

      <Spin spinning={loading}>
        <Row gutter={[12, 12]}>
          {MONTH_NAMES.map((name, idx) => {
            const month = idx + 1;
            const dirty = isDirty(month);
            const saved = !!rows[month];
            const isCurrent = year === currentYear && month === currentMonth;
            return (
              <Col xs={24} md={12} key={month}>
                <Card
                  size="small"
                  title={
                    <Space>
                      <Text strong>{name}</Text>
                      {/* "Mes actual" señala cuál es, no un estado: va en el
                          tinte de marca. Guardado/Sin guardar sí son estados y
                          usan la paleta semántica del CMS. */}
                      {isCurrent && <span className="section-tag">Mes actual</span>}
                      {saved && !dirty && <Tag color="green">Guardado</Tag>}
                      {dirty && <Tag color="orange">Sin guardar</Tag>}
                    </Space>
                  }
                  extra={
                    saved && (
                      <Popconfirm
                        title="¿Eliminar este lema?"
                        onConfirm={() => handleDelete(month)}
                        okText="Sí" cancelText="No"
                      >
                        <Button
                          size="small"
                          danger
                          type="text"
                          icon={<DeleteOutlined />}
                          disabled={savingMonth === month}
                        />
                      </Popconfirm>
                    )
                  }
                >
                  <Input.TextArea
                    rows={2}
                    maxLength={140}
                    showCount
                    placeholder="Ej.: Somos Comunidad Acogedora"
                    value={drafts[month] || ''}
                    onChange={(e) => setDrafts(prev => ({ ...prev, [month]: e.target.value }))}
                    onPressEnter={(e) => {
                      if (!e.shiftKey) {
                        e.preventDefault();
                        handleSave(month);
                      }
                    }}
                  />
                  <div style={{ marginTop: 28, textAlign: 'left' }}>
                    <Button
                      type="primary"
                      size="small"
                      icon={<SaveOutlined />}
                      loading={savingMonth === month}
                      disabled={!dirty || !(drafts[month] || '').trim()}
                      onClick={() => handleSave(month)}
                    >
                      Guardar
                    </Button>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      </Spin>
    </div>
  );
};

export default MottosPage;
